/**
 * Multiply an array a <count> number of times
 * @param arr : Array - Array to multiply
 * @param count : number - Number of times the array will be multiply
 * @returns any[] - Multiplied array
 */
export function multiplyArray(arr: any[], count: number): any[] {
  let result: any[] = [];

  for (let i = 0; i < count; i++) {
    result = [...result, ...arr];
  }

  return result;
}

/**
 * Groups items by a key, preserving the order in which groups first appear.
 *
 * @template T - The type of array elements.
 * @template K - The type of the key returned by the selector.
 * @param arr - The source array.
 * @param keySelector - Function that extracts the grouping key from each item.
 * @returns A Map where keys are the grouping keys and values are arrays of grouped items.
 *
 * @example
 * groupByOrdered(users, u => u.role);
 * // => Map { "admin" => [...], "user" => [...] }
 */
export function groupBy<T, K>(
  arr: T[],
  keySelector: (item: T) => K
): Map<K, T[]> {
  const map = new Map<K, T[]>();

  for (const item of arr) {
    const key = keySelector(item);
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)!.push(item);
  }

  return map;
}

/**
 * Returns the union of two arrays. Duplicates are removed.
 *
 * @template T - The type of array elements.
 * @param a - The first array.
 * @param b - The second array.
 * @returns A new array containing the unique items from both arrays.
 *
 * @example
 * union([1,2], [2,3]); // => [1,2,3]
 */
export function union<T>(a: T[], b: T[]): T[] {
  return [...new Set([...a, ...b])];
}

/**
 * Returns the difference between two arrays.
 * Items that are present in array `a` but not present in array `b`.
 *
 * An optional comparison function can be provided to define
 * custom equality logic (e.g. for objects).
 *
 * @template T - The type of array elements.
 * @param a - The source array.
 * @param b - The array containing elements to exclude.
 * @param compareFn - Optional comparison function that returns true
 * if two elements should be considered equal.
 *
 * @returns A new array containing elements from `a` that are not in `b`.
 *
 * @example
 * difference([1, 2, 3], [2]); // => [1, 3]
 *
 * @example
 * difference(
 *   [{ id: 1 }, { id: 2 }],
 *   [{ id: 2 }],
 *   (x, y) => x.id === y.id
 * ); // => [{ id: 1 }]
 */
export function difference<T>(
  a: T[],
  b: T[],
  compareFn?: (x: T, y: T) => boolean
): T[] {
  if (!compareFn) {
    const setB = new Set(b);
    return a.filter((x) => !setB.has(x));
  }

  return a.filter((x) => !b.some((y) => compareFn(x, y)));
}

/**
 * Returns the intersection of two arrays.
 * Items that are present in both arrays.
 *
 * An optional comparison function can be provided to define
 * custom equality logic (e.g. for objects).
 *
 * @template T - The type of array elements.
 * @param a - The first array.
 * @param b - The second array.
 * @param compareFn - Optional comparison function that returns true
 * if two elements should be considered equal.
 *
 * @returns A new array containing elements that exist in both arrays.
 *
 * @example
 * intersection([1, 2, 3], [2, 3, 4]); // => [2, 3]
 *
 * @example
 * intersection(
 *   [{ id: 1 }, { id: 2 }],
 *   [{ id: 2 }, { id: 3 }],
 *   (x, y) => x.id === y.id
 * ); // => [{ id: 2 }]
 */
export function intersection<T>(
  a: T[],
  b: T[],
  compareFn?: (x: T, y: T) => boolean
): T[] {
  if (!compareFn) {
    const setB = new Set(b);
    return a.filter((x) => setB.has(x));
  }

  return a.filter((x) => b.some((y) => compareFn(x, y)));
}

export function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

/**
 * Sorts an array of objects by a given key and direction.
 *
 * This method supports string, number, and ISO date string values.
 * For strings, it uses `localeCompare`. For numbers and ISO dates,
 * it performs a numeric comparison.
 *
 * @typeParam T - The type of elements in the array.
 *
 * @param array - The array of objects to sort. The array is mutated and also returned for convenience.
 * @param key - The object property to sort by.
 * @param direction - The desired sorting direction. Use `"asc"` for ascending order or `"desc"` for descending order.
 *
 * @returns The sorted array reference.
 *
 * @example
 * ```ts
 * const sorted = sortBy(invoices, "invoiceDate", "asc");
 * ```
 *
 * @example
 * ```ts
 * const sorted = sortBy(data, sortKey, sortDirection);
 * ```
 */
export function sortBy<T>(
  array: T[],
  key: keyof T,
  direction: 'asc' | 'desc'
): T[] {
  const factor = direction === 'asc' ? 1 : -1;

  return array.sort((a, b) => {
    const valA = a[key];
    const valB = b[key];

    // String comparison
    if (typeof valA === 'string' && typeof valB === 'string') {
      return valA.localeCompare(valB) * factor;
    }

    // Numeric comparison
    if (typeof valA === 'number' && typeof valB === 'number') {
      return (valA - valB) * factor;
    }

    // ISO date comparison
    const dateA = new Date(valA as any).getTime();
    const dateB = new Date(valB as any).getTime();
    if (!isNaN(dateA) && !isNaN(dateB)) {
      return (dateA - dateB) * factor;
    }

    return 0;
  });
}

export function partition<T>(
  array: T[],
  predicate: (item: T) => boolean
): [T[], T[]] {
  const trueItems: T[] = [];
  const falseItems: T[] = [];

  for (const item of array) {
    if (predicate(item)) {
      trueItems.push(item);
    } else {
      falseItems.push(item);
    }
  }

  return [trueItems, falseItems];
}
